import { useDateFormatter, useTranslations } from "@/contexts/Translation";
import { Card } from "@/types";
import styles from "./CardVerification.module.css";

// Anonymous answers are stored as <id>@anonymous.com. On the card they are one "Anonymous" chip.
const ANONYMOUS_DOMAIN = "anonymous.com";

// What the public card page proves: how many answered, when, and every survey from the first to
// the last, as a timeline. Domains are only shown when the owner exposes them.
const CardVerification = ({ card }: { card: Card }) => {
  const t = useTranslations("card");
  const d = useDateFormatter();
  const surveys = card.surveys || [];
  const domains = card.exposeDomains !== false ? card.stats?.domains || [] : [];
  const domainLabels = [...new Set(domains.map((dm) => (dm.domain === ANONYMOUS_DOMAIN ? t("anonymousDomain") : dm.domain)))];

  return (
    <section className={styles.verification} aria-labelledby='card-verification-heading' data-testid='card-verification'>
      <p className={styles.eyebrow}>{t("verifiedEyebrow")}</p>
      <h2 id='card-verification-heading' className={styles.heading}>
        {t("verifiedBuiltOn")}
      </h2>
      <dl className={styles.facts}>
        <div>
          <dt>{t("noOfResponders")}</dt>
          <dd>{card.responders}</dd>
        </div>
        <div>
          <dt>{t("latestResponse")}</dt>
          <dd>{card.latestResponse ? d(card.latestResponse) : "-"}</dd>
        </div>
        <div>
          <dt>{t("verifiedSurveys")}</dt>
          <dd>{card.stats?.noOfSurveys ?? surveys.length}</dd>
        </div>
      </dl>
      <p className={styles.series}>{t("verifiedSeries")}</p>
      {surveys.length > 0 && (
        <ol className={styles.timeline} data-testid='card-timeline'>
          {surveys.map((s, i) => (
            <li key={i} className={i === surveys.length - 1 ? styles.last : undefined}>
              <span className={styles.marker} aria-hidden='true'>
                {i === surveys.length - 1 && (
                  <svg viewBox='0 0 24 24' width='22' height='22'>
                    <path d='M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.2l7.1-.6z' />
                  </svg>
                )}
              </span>
              {s.surveyName && <span className={styles.surveyName}>{s.surveyName}</span>}
              <span className={styles.period}>
                {t("surveyPeriod", d(s.startDate), d(s.endDate))}
                {typeof s.responders === "number" && ` ${t("surveyAnswers", s.responders)}`}
              </span>
            </li>
          ))}
        </ol>
      )}
      {domainLabels.length > 0 && (
        <div className={styles.domains}>
          <h3 className={styles.domainsHeading}>{t("verifiedDomains")}</h3>
          <ul className={styles.chips}>
            {domainLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default CardVerification;
